import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router, browserHistory } from 'react-router';

import PartnersApp from 'partners';

import { muteReactWarnings } from 'utils/react';
import { getRootElement } from 'utils/element';

import StorefrontThemeEditorPage from 'containers/StorefrontThemeEditorPage';
import { STOREFRONT_EDITOR_CANVAS_PATH } from 'components/storefront/editor/utils/editor';

function isStorefrontThemeEditorCanvas() {
  return window.location.href.includes(STOREFRONT_EDITOR_CANVAS_PATH);
}

function isPartnerApp() {
  return window.location.hostname === process.env.PARTNER_HOST;
}

// partners.swell.store -> partners.swell.store/login
// parther.swell.store -> partners.swell.store/login
// partner.swell.store/path -> partners.swell.store/path
function redirectLegacyPartnerHost() {
  const canonicalHost = process.env.PARTNER_HOST;

  if (!canonicalHost) {
    return false;
  }

  const legacyHost = canonicalHost.replace(/^partners\./, 'partner.');
  const { hostname, protocol, port, pathname, search, hash } = window.location;

  const isCanonicalHost = hostname === canonicalHost;
  const isLegacyHost = legacyHost !== canonicalHost && hostname === legacyHost;

  if (!isCanonicalHost && !isLegacyHost) {
    return false;
  }

  const isRoot = pathname === '/';

  // The canonical host is already where it should be, unless it's the root
  if (isCanonicalHost && !isRoot) {
    return false;
  }

  const host = port ? `${canonicalHost}:${port}` : canonicalHost;
  const path = isRoot ? '/login' : pathname;

  window.location.replace(`${protocol}//${host}${path}${search}${hash}`);

  return true;
}

class App extends React.PureComponent {
  state = {
    loaded: false,
    store: null,
    routes: null,
  };

  async componentDidMount() {
    // Dynamically import modules to isolate CSS styles
    const [routes, store] = await Promise.all([
      import('./routes/instance'),
      import('./store/instance'),
      import('./styles/global.scss'),
    ]);

    this.setState({
      routes: routes.default,
      store: store.default,
      loaded: true,
    });
  }

  render() {
    const { loaded, store, routes } = this.state;

    if (!loaded) {
      return null;
    }

    return (
      <Provider store={store}>
        <Router history={browserHistory} routes={routes} />
      </Provider>
    );
  }
}

function getRootComponent() {
  if (isStorefrontThemeEditorCanvas()) {
    return <StorefrontThemeEditorPage />;
  }

  if (redirectLegacyPartnerHost()) {
    return null;
  }

  if (isPartnerApp()) {
    return <PartnersApp />;
  }

  return <App />;
}

muteReactWarnings();

ReactDOM.render(getRootComponent(), getRootElement());

if (module.hot) {
  module.hot.accept();
}
